import { WebContainer, type WebContainerProcess } from '@webcontainer/api';
import { workbenchStore } from '../stores/workbench';
import { appendTerminalLine, appendTerminalProcessOutput } from '../stores/terminal';
import { getHeadlessTemplateFiles } from '../templates/headlessTemplate';
import { materializeHotEditBridgeFilesInStore } from '../utils/mergedFiles';
import { wcPerfMark, wcPerfMeasure } from '../utils/workbenchPerf';
import { npmFrozenInstallCliArgs } from './npmFrozenInstall';
import {
  exportAndCacheNodeModules,
  fetchPrebuiltNodeModules,
  getPrebuiltNodeModulesUrl,
  getTemplateLockHash,
  mountDepsFromBuffer,
  readDepsCacheFromOpfs,
  removeDepsCacheFromOpfs,
} from './depsCache';

const PERSIST_KEY = '__ideozWorkbenchWebContainer' as const;

type PersistedWc = {
  instance: WebContainer | null;
  bootPromise: Promise<WebContainer> | null;
  templateReady: Promise<void> | null;
  lastPreviewUrl?: string;
  templateDepsInstalled?: boolean;
  productionBuildSucceeded?: boolean;
  devServerProcess?: WebContainerProcess | null;
  devServerRestartAttempts?: number;
  serverReadyTimeoutId?: ReturnType<typeof setTimeout> | null;
};

function getPersisted(): PersistedWc {
  const g = globalThis as typeof globalThis & { [PERSIST_KEY]?: PersistedWc };
  if (!g[PERSIST_KEY]) {
    g[PERSIST_KEY] = {
      instance: null,
      bootPromise: null,
      templateReady: null,
      devServerProcess: null,
      devServerRestartAttempts: 0,
      serverReadyTimeoutId: null,
    };
  }
  return g[PERSIST_KEY];
}

function restoreWorkbenchSessionAfterRemount(): void {
  const s = getPersisted();
  if (!s.instance) return;
  if (s.lastPreviewUrl && !workbenchStore.previewUrl.get()) {
    workbenchStore.previewUrl.set(s.lastPreviewUrl);
  }
  if (s.templateDepsInstalled) {
    workbenchStore.depsInstalled.set(true);
  }
  if (s.productionBuildSucceeded) {
    workbenchStore.buildStatus.set('success');
  }
}

export function syncWorkbenchStoreWithWebContainerPersist(): void {
  restoreWorkbenchSessionAfterRemount();
}

export function markTemplateDependenciesInstalled(): void {
  getPersisted().templateDepsInstalled = true;
}

export function recordProductionBuildOutcome(ok: boolean): void {
  getPersisted().productionBuildSucceeded = ok;
}

/**
 * Clear the cross-remount build/preview cache (`lastPreviewUrl`, `productionBuildSucceeded`,
 * `templateDepsInstalled`) without touching the WebContainer instance itself — the instance
 * and dev server are a single `globalThis` singleton shared across every conversation in the
 * tab, but this cache is meant to survive only a same-conversation remount (e.g. reopening the
 * prototype panel). `workbenchStore.reset()` clears the *reactive* store on every remount, but
 * never touched this separate cache, so `restoreWorkbenchSessionAfterRemount` would immediately
 * re-apply a previous, unrelated conversation's "build succeeded" / preview URL right back on
 * top of the reset — call this once, on a genuine conversation switch (not on every remount),
 * so the next remount's restore has nothing stale left to re-apply.
 */
export function resetPersistedBuildCache(): void {
  const s = getPersisted();
  s.lastPreviewUrl = undefined;
  // Keep templateDepsInstalled — node_modules belongs to the shared WebContainer
  // singleton, not to a conversation. Clearing it here left depsInstalled false
  // after workbenchStore.reset() on remount with no path to set it again.
  s.productionBuildSucceeded = false;
}

export type SnapshotPreviewFinalizeOptions = {
  buildVerified?: boolean;
  knownBuildSuccess?: boolean;
};

export function markSnapshotPreviewReady(options?: SnapshotPreviewFinalizeOptions): void {
  workbenchStore.snapshotPreviewReady.set(true);
  if (options?.buildVerified === true || options?.knownBuildSuccess === true) {
    workbenchStore.buildStatus.set('success');
    recordProductionBuildOutcome(true);
  }
  syncWorkbenchStoreWithWebContainerPersist();
}

async function killDevServer(): Promise<void> {
  const s = getPersisted();
  if (s.serverReadyTimeoutId != null) {
    clearTimeout(s.serverReadyTimeoutId);
    s.serverReadyTimeoutId = null;
  }
  const proc = s.devServerProcess;
  if (!proc) return;
  s.devServerProcess = null;
  try {
    await proc.kill();
  } catch {
    /* already dead */
  }
}

export function restartDevServerAfterSnapshot(wc: WebContainer): Promise<void> {
  return startDevServer(wc, { forceRestart: true });
}

const PREVIEW_PORT = 3111;

type ViteServerKind = 'dev' | 'preview';

/**
 * `WebContainer.boot()` occasionally never settles — the SDK's own worker handshake
 * can wedge (visible in devtools as its fetch worker being preloaded but never used)
 * with no rejection, no timeout of its own, and no way to cancel it. Without this
 * guard the boot promise hangs forever: `bootStatus` never leaves 'booting', nothing
 * is ever shown, and — since the wedge lives in the browser's WebContainer/worker
 * layer rather than any state this app owns — even a plain page refresh doesn't
 * clear it. Racing it against a deadline turns that silent hang into a bounded,
 * user-visible error the existing boot `.catch` (in usePrototyperWorkbenchFacade)
 * can surface and let the user retry.
 */
const WEBCONTAINER_BOOT_TIMEOUT_MS = 45_000;

export async function bootWebContainer(): Promise<WebContainer> {
  const s = getPersisted();
  if (s.instance) {
    restoreWorkbenchSessionAfterRemount();
    return s.instance;
  }

  if (!globalThis.crossOriginIsolated) {
    throw new Error(
      '[Workbench] crossOriginIsolated is false — WebContainer requires COOP/COEP headers. ' +
        'Ensure Cross-Origin-Opener-Policy: same-origin and Cross-Origin-Embedder-Policy: credentialless are set.',
    );
  }

  if (!s.bootPromise) {
    wcPerfMark('wc:boot:start');
    const bootAttempt = WebContainer.boot()
      .then((wc) => {
        wcPerfMark('wc:boot:end');
        wcPerfMeasure('wc:boot:start', 'wc:boot:end', 'wc:WebContainer.boot');
        s.instance = wc;
        wc.on('server-ready', (_port, url) => {
          if (s.serverReadyTimeoutId != null) {
            clearTimeout(s.serverReadyTimeoutId);
            s.serverReadyTimeoutId = null;
          }
          // A crash-episode's 1-retry budget should reset once the server proves it's
          // healthy again — otherwise two unrelated crashes anywhere in the tab's life
          // permanently disable auto-restart for every crash after that.
          s.devServerRestartAttempts = 0;
          s.lastPreviewUrl = url;
          workbenchStore.previewUrl.set(url);
          workbenchStore.devServerStatus.set('ready');
          wcPerfMark('wc:timeToFirstPreview');
          restoreWorkbenchSessionAfterRemount();
        });
        s.templateReady = mountHeadlessTemplate(wc).catch((err: unknown) => {
          s.templateReady = null;
          // A cold first-time generation never calls `waitForTemplateReady()` — nothing else
          // is awaiting this promise, so without this it's an unhandled rejection and the UI
          // sits on "Setting up your prototype workspace…" (`!depsInstalled`) forever with no
          // error. Publish to the store directly so `WorkbenchPanel`'s existing boot-error UI
          // (Retry/Refresh) picks it up regardless of who — if anyone — is awaiting this.
          workbenchStore.bootStatus.set('error');
          workbenchStore.bootError.set(
            err instanceof Error ? err.message : 'Failed to set up the prototype workspace',
          );
          throw err;
        });
        return wc;
      })
      .catch((err: unknown) => {
        if (s.bootPromise === p) s.bootPromise = null;
        throw err;
      });

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        reject(
          new Error(
            `WebContainer did not finish booting within ${Math.round(WEBCONTAINER_BOOT_TIMEOUT_MS / 1000)}s. ` +
              'This can happen when a previous WebContainer session in this tab did not shut down cleanly — ' +
              'try closing other tabs running the workbench, or refresh the page.',
          ),
        );
      }, WEBCONTAINER_BOOT_TIMEOUT_MS);
    });

    const p: Promise<WebContainer> = Promise.race([bootAttempt, timeout]).finally(() => {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    });
    // The timeout branch rejects without touching `bootAttempt`'s own `.catch` above,
    // so it must clear `s.bootPromise` itself — otherwise a timed-out boot leaves
    // `s.bootPromise` permanently wedged and every future call just re-awaits it.
    p.catch(() => {
      if (s.bootPromise === p) s.bootPromise = null;
    });
    s.bootPromise = p;
  }

  return s.bootPromise.then((wc) => {
    restoreWorkbenchSessionAfterRemount();
    return wc;
  });
}

/**
 * Ceiling for the whole template mount, deliberately well above `TEMPLATE_INSTALL_TIMEOUT_MS`
 * (180s) so a legitimately slow cold `npm ci` is never cut short. This is not a UX timeout —
 * the UI escapes far sooner — it exists purely so this promise *always* settles.
 *
 * Why it's needed: `mountHeadlessTemplate` has exactly one bounded await (`proc.exit`). Every
 * other step — the OPFS deps-cache read, `mountDepsFromBuffer`'s `chmod`, the prebuilt-deps
 * `fetch`, `install.output.pipeTo`, and `wc.spawn('npx', ['vite', …])` — can stall forever with
 * no rejection. A template *failure* is recoverable (the `.catch` on `templateReady` sets
 * `bootStatus: 'error'`, which surfaces retry UI); a template *hang* produces no state
 * transition anywhere, so every `waitForTemplateReady()` caller blocks for the life of the page.
 * Racing a deadline converts that silent hang into a normal, recoverable error.
 */
const TEMPLATE_READY_TIMEOUT_MS = 240_000;

export async function waitForTemplateReady(): Promise<void> {
  const s = getPersisted();
  await s.bootPromise;
  if (!s.templateReady) return;

  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(
        new Error(
          `Preparing the prototype workspace did not finish within ${Math.round(
            TEMPLATE_READY_TIMEOUT_MS / 1000,
          )}s. Retry, or reload the page.`,
        ),
      );
    }, TEMPLATE_READY_TIMEOUT_MS);
  });

  try {
    await Promise.race([s.templateReady, timeout]);
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
}

export async function retryTemplateMount(): Promise<void> {
  const s = getPersisted();
  if (!s.instance) {
    await bootWebContainer();
    return;
  }
  s.templateReady = mountHeadlessTemplate(s.instance).catch((err: unknown) => {
    s.templateReady = null;
    throw err;
  });
  await s.templateReady;
}

export function getWebContainer(): WebContainer | null {
  return getPersisted().instance;
}

export function teardownWebContainer(): void {
  const s = getPersisted();
  void killDevServer();
  if (s.instance) {
    s.instance.teardown();
    s.instance = null;
    s.bootPromise = null;
    s.templateReady = null;
    s.lastPreviewUrl = undefined;
    s.templateDepsInstalled = undefined;
    s.productionBuildSucceeded = undefined;
  }
}

const TEMPLATE_FS_CONCURRENCY = 14;

/**
 * The initial template's `npm ci`/`npm install` is a real spawned process with no timeout of
 * its own — if WebContainer's virtual registry/network layer wedges (the same underlying
 * instability that can strand `WebContainer.boot()` itself, see `WEBCONTAINER_BOOT_TIMEOUT_MS`
 * above), `install.exit` never settles, `depsInstalled` never flips true, and the UI is stuck on
 * "Setting up your prototype workspace…" forever with no error and no recovery short of clearing
 * site data. Mirrors `waitForExitWithTimeout` in `runtime/tool-adapters/build.ts` (not reused
 * directly to avoid a cross-module dependency for a 10-line helper).
 */
const TEMPLATE_INSTALL_TIMEOUT_MS = 180_000;

async function waitForExitWithTimeout(
  proc: WebContainerProcess,
  timeoutMs: number,
  label: string,
): Promise<number> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      void proc.kill();
      reject(new Error(`${label} did not finish within ${Math.round(timeoutMs / 1000)}s and was killed.`));
    }, timeoutMs);
  });
  try {
    return await Promise.race([proc.exit, timeout]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

async function runPool<T>(items: T[], limit: number, fn: (item: T) => Promise<void>): Promise<void> {
  if (items.length === 0) return;
  let i = 0;
  const worker = async (): Promise<void> => {
    for (;;) {
      const idx = i++;
      if (idx >= items.length) break;
      await fn(items[idx]);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, () => worker()),
  );
}

async function installTemplateDependencies(wc: WebContainer, files: Record<string, string>): Promise<void> {
  const lockContent = files['package-lock.json'];
  const lockHash = lockContent ? getTemplateLockHash(lockContent) : 'no-lock';

  if (lockContent) {
    const opfsBuf = await readDepsCacheFromOpfs(lockHash);
    if (opfsBuf && (await mountDepsFromBuffer(wc, opfsBuf))) {
      appendTerminalLine('Restored node_modules from OPFS cache.', 'info');
      workbenchStore.depsInstalled.set(true);
      markTemplateDependenciesInstalled();
      return;
    }
    if (opfsBuf) {
      void removeDepsCacheFromOpfs(lockHash);
    }

    const prebuiltUrl = getPrebuiltNodeModulesUrl(lockHash);
    const prebuiltBuf = await fetchPrebuiltNodeModules(lockHash, prebuiltUrl);
    if (prebuiltBuf && (await mountDepsFromBuffer(wc, prebuiltBuf))) {
      appendTerminalLine('Restored node_modules from prebuilt snapshot.', 'info');
      workbenchStore.depsInstalled.set(true);
      markTemplateDependenciesInstalled();
      void exportAndCacheNodeModules(wc, lockHash);
      return;
    }
  }

  const hasPkgLock = Boolean(files['package-lock.json']);
  const npmArgs = npmFrozenInstallCliArgs(hasPkgLock);
  appendTerminalLine(`Template mounted. Running npm ${npmArgs[0]}…`, 'info');

  const npmStart = hasPkgLock ? 'wb:npm:ci:start' : 'wb:npm:install:start';
  const npmEnd = 'wb:npm:template:end';
  wcPerfMark(npmStart);
  const install = await wc.spawn('npm', npmArgs);
  const chunks: string[] = [];
  const writer = new WritableStream<string>({
    write(chunk) {
      chunks.push(chunk);
    },
  });
  const installPiping = install.output.pipeTo(writer);
  const exitCode = await waitForExitWithTimeout(
    install,
    TEMPLATE_INSTALL_TIMEOUT_MS,
    `npm ${npmArgs[0]}`,
  );
  await installPiping;
  appendTerminalProcessOutput(chunks.join(''), exitCode !== 0 ? 'stderr' : 'stdout');
  wcPerfMark(npmEnd);
  wcPerfMeasure(npmStart, npmEnd, hasPkgLock ? 'wc:template:npmCi' : 'wc:template:npmInstall');

  if (exitCode !== 0) {
    appendTerminalLine(`npm ${npmArgs[0]} failed (exit ${exitCode})`, 'stderr');
    throw new Error(`npm ${npmArgs[0]} failed:\n${chunks.join('')}`);
  }

  workbenchStore.depsInstalled.set(true);
  markTemplateDependenciesInstalled();
  appendTerminalLine('Dependencies installed.', 'info');

  if (lockContent) {
    void exportAndCacheNodeModules(wc, lockHash);
  }
}

async function mountHeadlessTemplate(wc: WebContainer): Promise<void> {
  const files = getHeadlessTemplateFiles();
  const paths = Object.keys(files);

  appendTerminalLine(`Mounting headless template (${paths.length} files)…`, 'info');

  wcPerfMark('wc:templateFs:start');
  const dirs = new Set<string>();
  for (const filePath of paths) {
    const dir = filePath.includes('/') ? filePath.substring(0, filePath.lastIndexOf('/')) : '';
    if (dir) dirs.add(dir);
  }
  const sortedDirs = [...dirs].sort((a, b) => a.split('/').length - b.split('/').length);
  for (const d of sortedDirs) {
    await wc.fs.mkdir(d, { recursive: true });
  }
  await runPool(paths, TEMPLATE_FS_CONCURRENCY, (filePath) => wc.fs.writeFile(filePath, files[filePath]));
  wcPerfMark('wc:templateFs:end');
  wcPerfMeasure('wc:templateFs:start', 'wc:templateFs:end', 'wc:mount:templateFsWrites');

  await installTemplateDependencies(wc, files);

  materializeHotEditBridgeFilesInStore();

  await startDevServer(wc);
}

async function spawnViteServer(
  wc: WebContainer,
  kind: ViteServerKind,
  opts?: { forceRestart?: boolean },
): Promise<void> {
  const s = getPersisted();
  if (opts?.forceRestart) {
    s.devServerRestartAttempts = 0;
    await killDevServer();
  }

  workbenchStore.devServerStatus.set('starting');
  const npxArgs =
    kind === 'dev'
      ? ['vite', '--port', String(PREVIEW_PORT), '--host']
      : ['vite', 'preview', '--port', String(PREVIEW_PORT), '--host'];
  appendTerminalLine(`$ npx ${npxArgs.join(' ')}`, 'info');

  if (s.serverReadyTimeoutId != null) {
    clearTimeout(s.serverReadyTimeoutId);
  }
  s.serverReadyTimeoutId = setTimeout(() => {
    if (workbenchStore.devServerStatus.get() === 'starting') {
      workbenchStore.devServerStatus.set('error');
      const label = kind === 'dev' ? 'Dev server' : 'Preview server';
      appendTerminalLine(`${label} did not become ready within 60s.`, 'stderr');
    }
  }, 60_000);

  try {
    const proc = await wc.spawn('npx', npxArgs);
    s.devServerProcess = proc;

    void proc.output.pipeTo(
      new WritableStream<string>({
        write(chunk) {
          appendTerminalLine(chunk);
        },
      }),
    );

    void proc.exit.then(async (code) => {
      if (s.devServerProcess !== proc) return;
      s.devServerProcess = null;
      if (code !== 0) {
        workbenchStore.devServerStatus.set('crashed');
        const label = kind === 'dev' ? 'Dev server' : 'Preview server';
        appendTerminalLine(`${label} exited with code ${code}`, 'stderr');
        if (kind === 'dev') {
          const attempts = (s.devServerRestartAttempts ?? 0) + 1;
          s.devServerRestartAttempts = attempts;
          if (attempts <= 1) {
            appendTerminalLine('Restarting dev server…', 'info');
            await spawnViteServer(wc, 'dev', { forceRestart: true });
          }
        }
      }
    });
  } catch (err: unknown) {
    workbenchStore.devServerStatus.set('error');
    const label = kind === 'dev' ? 'dev server' : 'preview server';
    appendTerminalLine(
      `Failed to start ${label}: ${err instanceof Error ? err.message : String(err)}`,
      'stderr',
    );
  }
}

async function startDevServer(
  wc: WebContainer,
  opts?: { forceRestart?: boolean },
): Promise<void> {
  await spawnViteServer(wc, 'dev', opts);
}

/** Start `vite preview` when production build succeeded but no preview URL is available. */
export async function ensureProductionPreviewServer(wc: WebContainer): Promise<void> {
  await waitForTemplateReady();
  const url = workbenchStore.previewUrl.get();
  const status = workbenchStore.devServerStatus.get();
  if (url && status === 'ready') {
    return;
  }
  await spawnViteServer(wc, 'preview', { forceRestart: true });
}

export interface InteractiveShell {
  write: (data: string) => void;
  resize: (cols: number, rows: number) => void;
  kill: () => Promise<void>;
}

export async function startInteractiveShell(
  wc: WebContainer,
  opts: {
    cols: number;
    rows: number;
    onData: (chunk: string) => void;
    onExit?: (code: number) => void;
  },
): Promise<InteractiveShell> {
  await waitForTemplateReady();

  const process = await wc.spawn('jsh', {
    terminal: { cols: opts.cols, rows: opts.rows },
  });

  const inputWriter = process.input.getWriter();

  void process.output.pipeTo(
    new WritableStream<string>({
      write(chunk) {
        opts.onData(chunk);
      },
    }),
  );

  void process.exit.then((code) => opts.onExit?.(code));

  return {
    write: (data: string) => {
      void inputWriter.write(data);
    },
    resize: (cols: number, rows: number) => {
      (process as unknown as { resize?: (d: { cols: number; rows: number }) => void }).resize?.({
        cols,
        rows,
      });
    },
    kill: () => {
      const r = process.kill() as unknown;
      return r instanceof Promise ? r : Promise.resolve();
    },
  };
}
